import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  ActivatePartnerOfferInput,
  AnalyticsEvent,
  ButtonComponentActionResultTypenames,
  ContextualColor,
  ContextualIcon,
  ItemType,
  Mutation,
  OfferStatus,
  Tag,
  TagColor,
  UpsideOfferDetails,
  VxlButtonStyle,
} from '@varo/graphql-schema-types';
import { getCalendars } from 'expo-localization';
import { useRouter } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import BoostInfoBottomSheet from 'features/rewards/components/BoostInfoBottomSheet/BoostInfoBottomSheet';
import DeactivateBottomSheet from 'features/rewards/components/DeactivateBottomSheet/DeactivateBottomSheet';
import DynamicMapCard from 'features/rewards/components/MapCard/DynamicMapCard';
import MaximumReachedBottomSheet from 'features/rewards/components/MaximumReachedBottomSheet/MaximumReachedBottomSheet';
import OfferActivatedBottomSheet from 'features/rewards/components/OfferActivatedBottomSheet/OfferActivatedBottomSheet';
import GasOfferCardCarousel from 'features/rewards/components/rewardsCarousels/GasOfferCardCarousel';
import TitleLockUp from 'features/rewards/components/TitleLockUp/TitleLockUp';
import { INLINE_ALERT_IDS } from 'features/rewards/constants/constants';
import RenderHtmlOfferSectionView from 'features/rewards/Details/RenderHtmlOfferSectionView/RenderHtmlOfferSectionView';
import { buildLocalOfferLocationFromUpsideDetails } from 'features/rewards/helpers/buildLocalOfferLocationFromUpsideDetails.helpers';
import { openMap } from 'features/rewards/helpers/openMap.helpers';
import useUpsideOffersDetails from 'features/rewards/hooks/useUpsideOfferDetails';
import {
  isActivationError as isActivationErrorResult,
  isActivationMaximumReached,
  isActivationSuccess as isActivationSuccessResult,
  isDailyRedemptionMaxReached,
} from 'features/rewards/screens/CashbackHomeDetails/UpsideOfferDetails.helpers';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  GQLAnalytics,
  parseOptionalGQLAnalyticsEvent,
} from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { ActivatePartnerOffer } from 'services/graphql/mutations/Cashback/ActivatePartnerOffer';
import Button from 'shared/components/vxl/Button/Button';
import ContentList from 'shared/components/vxl/ContentList/ContentList';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import {
  ListItemLineFormat,
  ListItemTextStackAlignment,
} from 'shared/components/vxl/ListItem/ListItemTextStack';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import { isWeb } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import {
  useViewDidFocusLogScreenAnalytics,
  useViewDidFocusScreenAnalyticsEvent,
} from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import TabDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/TabDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { capitalizeWord } from 'shared/utils/string/string.helpers';

/*eslint-disable max-lines*/

interface UpsideOfferDetailsProps {
  offerId: string;
  merchantId: string;
  coordinates?: google.maps.LatLngLiteral;
  autoActivate?: boolean;
  categoryName?: string;
}

const onTopAllowedAlertIds = [
  INLINE_ALERT_IDS.PENDING_ALERT,
  INLINE_ALERT_IDS.BOOST_ALERT,
];

const UpsideDetailsScreen = ({
  offerId,
  merchantId,
  coordinates,
  autoActivate,
  categoryName,
}: UpsideOfferDetailsProps) => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'upsideOffers',
  });
  const { t: translatedDeactivateText } = useTranslation('rewards', {
    keyPrefix: 'deactivateBottomSheet',
  });

  const activateOfferRef = useRef<BottomSheetModal | null>(null);
  const boostSheetRef = useRef<BottomSheetModal | null>(null);
  const maximumReachedBottomSheetRef = useRef<BottomSheetModal | null>(null);
  const snackbarTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const insets = useSafeAreaInsets();
  const deactivateBottomSheetRef = useRef<BottomSheetModal | null>(null);
  const hasAutoActivatedRef = useRef(false);
  const isActivatingRef = useRef(false);
  const [showFailureSnackbar, setShowFailureSnackbar] = useState(false);
  const [failureSnackbarText, setFailureSnackbarText] = useState('');
  const [activationSuccessAnalytics, setActivationSuccessAnalytics] =
    useState<GQLAnalytics | null>(null);
  const [hasDeactivationError, setHasDeactivationError] = useState(false);
  const hasValidParams = Boolean(offerId && merchantId);

  const { loading, offer, error, stale, paused, refetchOfferDetails } =
    useUpsideOffersDetails(merchantId, coordinates, categoryName);

  const displayAnalytics = parseOptionalGQLAnalyticsEvent(
    offer?.displayAnalytics,
  );
  useViewDidFocusLogScreenAnalytics(displayAnalytics);

  const { segmentClient } = useAnalyticsConfiguration();

  const gasOffers = offer?.pricingTiers;

  const howItWorks = offer?.howItWorks;

  const offerAlerts = offer?.inLineAlerts ?? [];

  const onTopAlerts = offerAlerts
    .filter(
      (alert) =>
        alert.id && onTopAllowedAlertIds.some((id) => alert.id.startsWith(id)),
    )
    .sort((a, b) => {
      const indexA = onTopAllowedAlertIds.findIndex((id) =>
        a.id?.startsWith(id),
      );
      const indexB = onTopAllowedAlertIds.findIndex((id) =>
        b.id?.startsWith(id),
      );

      return indexA - indexB;
    });

  const convenientStoreAlert = offerAlerts.find(
    (alert) =>
      alert.id && alert.id.startsWith(INLINE_ALERT_IDS.CONVENIENCE_STORE_ALERT),
  );

  const localOfferLocation = offer
    ? buildLocalOfferLocationFromUpsideDetails(offer)
    : null;

  const hasCoordinateNumbers = (
    latitude: number | undefined,
    longitude: number | undefined,
  ) => {
    return typeof latitude === 'number' && typeof longitude === 'number';
  };

  const hasCenter = hasCoordinateNumbers(
    offer?.mapData?.center?.latitude,
    offer?.mapData?.center?.longitude,
  );

  const hasLocal = hasCoordinateNumbers(
    localOfferLocation?.coordinate?.latitude,
    localOfferLocation?.coordinate?.longitude,
  );

  const getCenterLocation = (): { lat: number; lng: number } | null => {
    if (hasCenter && offer?.mapData?.center) {
      return {
        lat: offer.mapData.center.latitude,
        lng: offer.mapData.center.longitude,
      };
    }
    if (hasLocal && localOfferLocation?.coordinate) {
      return {
        lat: localOfferLocation.coordinate.latitude,
        lng: localOfferLocation.coordinate.longitude,
      };
    }
    return null;
  };

  const centerLocation = getCenterLocation();

  type ActivatePartnerOfferMutationVariables = {
    input: ActivatePartnerOfferInput;
  };

  const [mutationResult, executeMutation] = useCancellableMutation<
    Mutation,
    ActivatePartnerOfferMutationVariables
  >(ActivatePartnerOffer);

  const {
    data: mutationData,
    error: mutationError,
    fetching: mutationFetching,
  } = mutationResult;

  const activationResult = mutationData?.activatePartnerOffer;

  const isActivationSuccess = isActivationSuccessResult(activationResult);
  const isActivationError = isActivationErrorResult(activationResult);
  const activationMaxReached = isActivationMaximumReached(activationResult);
  const dailyMaxReached = isDailyRedemptionMaxReached(activationResult);

  const [deviceCalendar] = getCalendars();
  const deviceTimeZone =
    deviceCalendar.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const shortTz = new Intl.DateTimeFormat('en-us', {
    timeZone: deviceTimeZone,
    timeZoneName: 'short',
  })
    .format(new Date())
    .split(' ')
    .pop();

  const offerActivationDetails = isActivationSuccess
    ? {
        successTitle: activationResult.title ?? '',
        instructions: (activationResult.instructions ?? []).map((itemData) => {
          const baseText = itemData.text ?? '';
          const fullText = itemData.expiryDay
            ? `${baseText} ${shortTz} ${itemData.expiryDay.toLowerCase()}`
            : baseText;
          return {
            id: itemData.id ?? '',
            icon: itemData.icon ?? 'ELLIPSE',
            text: fullText,
          };
        }),
        primaryButton: {
          buttonStyle:
            activationResult.primaryButton?.buttonStyle ??
            VxlButtonStyle.Primary,
          buttonText:
            activationResult.primaryButton?.buttonText ??
            translatedText('getDirections'),
        },
      }
    : {
        successTitle: '',
        instructions: [],
        primaryButton: {
          buttonStyle: VxlButtonStyle.Primary,
          buttonText: translatedText('getDirections'),
        },
      };

  useViewDidFocusLogScreenAnalytics(
    isActivationSuccess && activationResult?.displayAnalytics
      ? parseOptionalGQLAnalyticsEvent(activationResult.displayAnalytics)
      : null,
  );

  const legalText = translatedText('legalText');

  const listData = [
    offer?.redemptionLocationInfo,
    offer?.cardUsageInfo,
    offer?.maxEarningsInfo,
    offer?.singleUseInfo,
  ].filter(Boolean);

  const getHtmlContents = (offerData: UpsideOfferDetails) => {
    let disclaimerHtml = offerData.disclosure?.replace(/\n/g, '<br />');
    return [
      {
        id: 'about',
        title: offerData.termsTitle,
        content: offerData.terms ? `${offerData.terms}` : undefined,
        showDivider: true,
        linkText: undefined,
      },
      {
        id: 'disclaimer',
        title: offerData.disclosureTitle,
        content: disclaimerHtml,
        showDivider: false,
        linkText: legalText,
      },
    ];
  };

  const offerTag: Tag = {
    __typename: 'Tag',
    id: 'offer-tag',
    color: ContextualColor.Info,
    tagColor:
      offer?.tag?.tagColor === TagColor.Positive
        ? TagColor.Positive
        : TagColor.Info,
    text: offer?.tag?.text ?? translatedText('activateToUse'),
    icon:
      offer?.tag?.tagColor === TagColor.Positive
        ? ContextualIcon.ListCheckmark
        : null,
  };

  const handlePdfClick = () => {
    router.push({
      pathname: '/rewards/disclosure',
      params: {
        pdfUrl: translatedText('pdfUrl'),
      },
    });
  };

  const logAnalyticsForButtonClick = (
    buttonOnClick:
      | { __typename?: string; analyticsEvent?: AnalyticsEvent | null }
      | null
      | undefined,
    expectedTypename: string,
  ): void => {
    if (
      buttonOnClick?.__typename !== expectedTypename ||
      !buttonOnClick?.analyticsEvent
    ) {
      return;
    }
    try {
      const analytics = parseOptionalGQLAnalyticsEvent(
        buttonOnClick.analyticsEvent,
      );

      if (analytics) {
        void segmentClient.logEvent(analytics.name, analytics.properties);
      }
    } catch (error) {
      logErrorWithContext(error, {
        featureArea: FeatureArea.REWARDS,
        responsibleTeam: ResponsibleTeam.CASHBACK_REWARDS,
        severity: ErrorSeverity.MEDIUM,
      });
    }
  };

  const activateOffer = async (
    offerId?: string | null,
    merchantId?: string | null,
    companionOfferId?: string | null,
  ): Promise<void> => {
    if (!offerId || !merchantId || isActivatingRef.current) {
      return;
    }

    isActivatingRef.current = true;

    try {
      await executeMutation({
        input: {
          offerId,
          merchantId,
          companionOfferId,
        },
      });
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
    } finally {
      isActivatingRef.current = false;
    }
  };

  const handleOnActivateClick = () => {
    logAnalyticsForButtonClick(
      offer?.primaryButton?.buttonOnClick,
      ButtonComponentActionResultTypenames.ActivateOfferAction,
    );
    activateOffer(offer?.id, offer?.merchantId, offer?.companionOfferId);
  };

  const handleMaximumReachedSubmit = (): void => {
    if (activationMaxReached) {
      logAnalyticsForButtonClick(
        activationResult.button?.buttonOnClick,
        ButtonComponentActionResultTypenames.OpenURLNavigationAction,
      );
    }

    maximumReachedBottomSheetRef.current?.dismiss();
    if (activationMaxReached) {
      router.push({
        pathname: '/rewards/my-offers',
        params: { listType: 'activated' },
      });
    } else if (dailyMaxReached) {
      router.push({ pathname: '/rewards/cashback/cashbackEarned' });
    }
  };

  const handleGetDirectionsClick = () => {
    logAnalyticsForButtonClick(
      offer?.primaryButton?.buttonOnClick,
      ButtonComponentActionResultTypenames.OpenURLNavigationAction,
    );
    if (centerLocation) {
      openMap(centerLocation.lat, centerLocation.lng);
    }
  };

  const handleOnDeactivateClick = () => {
    logAnalyticsForButtonClick(
      offer?.secondaryButton?.buttonOnClick,
      ButtonComponentActionResultTypenames.DeactivateOfferAction,
    );
    deactivateBottomSheetRef.current?.present();
  };

  useViewDidFocusLogScreenAnalytics(activationSuccessAnalytics);

  useViewDidFocusLogScreenAnalytics(
    activationMaxReached && activationResult?.displayAnalytics
      ? parseOptionalGQLAnalyticsEvent(activationResult.displayAnalytics)
      : null,
  );

  useViewDidFocusScreenAnalyticsEvent(
    isActivationError || mutationError
      ? { key: '/rewards/upside-offer/activation-error' }
      : null,
  );

  useViewDidFocusScreenAnalyticsEvent(
    hasDeactivationError
      ? { key: '/rewards/upside-offer/deactivation-error' }
      : null,
  );

  const handleDeactivateConfirm = () => {
    deactivateBottomSheetRef.current?.dismiss();
    router.back();
  };

  const handleDeactivateError = (message?: string) => {
    setHasDeactivationError(true);
    setFailureSnackbarText(message ?? translatedDeactivateText('snackbarText'));
    setShowFailureSnackbar(true);
    if (snackbarTimeoutRef.current) {
      clearTimeout(snackbarTimeoutRef.current);
    }
    snackbarTimeoutRef.current = setTimeout(() => {
      setShowFailureSnackbar(false);
      setHasDeactivationError(false);
    }, 5000);
  };

  const getPrimaryButtonHandler = () => {
    const buttonActionType = offer?.primaryButton?.buttonOnClick.__typename;
    if (
      buttonActionType ===
      ButtonComponentActionResultTypenames.ActivateOfferAction
    ) {
      return handleOnActivateClick;
    }

    return handleGetDirectionsClick;
  };

  useEffect(() => {
    if (isActivationSuccess) {
      hasAutoActivatedRef.current = true;
      setTimeout(() => {
        activateOfferRef.current?.present?.();
      }, 0);
    }
  }, [isActivationSuccess]);

  useEffect(() => {
    if (activationMaxReached || dailyMaxReached) {
      maximumReachedBottomSheetRef.current?.present?.();
    }
  }, [activationMaxReached, dailyMaxReached]);

  useEffect(() => {
    if (hasAutoActivatedRef.current) {
      return;
    }

    const canAutoActivate =
      autoActivate &&
      offer &&
      offer.id &&
      offer.merchantId &&
      !loading &&
      offer.status !== OfferStatus.Activated;

    if (!canAutoActivate) {
      return;
    }

    void activateOffer(offer.id, offer.merchantId, offer.companionOfferId);
  }, [offer]);

  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (snackbarTimeoutRef.current) {
        clearTimeout(snackbarTimeoutRef.current);
      }
    };
  }, []);

  const reload = (): void => {
    setTimeout(() => {
      refetchOfferDetails({ requestPolicy: 'network-only' });
    }, 0);
  };

  return (
    <View style={styles.container}>
      <StatusBar style="dark" />
      {!isWeb ? (
        <NavigationBar
          title=""
          primaryIcon={<Icon iconKey="CLOSE" iconSize="MD" />}
          primaryOnPress={() => router.back()}
        />
      ) : null}
      <View
        style={[styles.scrollViewContainer, { paddingBottom: insets.bottom }]}
      >
        <ScrollView style={styles.scrollStyle}>
          {offer ? (
            <>
              <View style={styles.offerInfoContainer}>
                {onTopAlerts.length > 0 ? (
                  <View style={styles.alertContainer}>
                    {onTopAlerts.map((alert) => {
                      const isBoostAlert = alert.id?.startsWith(
                        INLINE_ALERT_IDS.BOOST_ALERT,
                      );
                      return (
                        <InlineAlert
                          key={alert.id}
                          alertType={alert.alertType}
                          iconKeyType={alert.icon}
                          titleText={alert.title ?? ''}
                          bodyText={alert.body ?? ''}
                          onCardPress={
                            isBoostAlert
                              ? () => boostSheetRef.current?.present()
                              : undefined
                          }
                        />
                      );
                    })}
                  </View>
                ) : null}

                <TitleLockUp
                  logoUrl={offer?.logoImage}
                  merchant={offer?.name || ' '}
                  offerRate={offer?.shortDescription || ' '}
                  tag={offerTag}
                />

                {convenientStoreAlert ? (
                  <View style={styles.convenientAlertContainer}>
                    <InlineAlert
                      alertType={convenientStoreAlert.alertType}
                      iconKeyType={convenientStoreAlert.icon}
                      titleText={convenientStoreAlert.title ?? ''}
                      bodyText={convenientStoreAlert.body ?? ''}
                    />
                  </View>
                ) : null}

                {centerLocation ? (
                  <DynamicMapCard
                    id={offer.id}
                    markerData={localOfferLocation ? [localOfferLocation] : []}
                    cameraLocation={centerLocation}
                    onMarkerPress={() => {}}
                    onMapCardPress={() =>
                      openMap(centerLocation.lat, centerLocation.lng)
                    }
                    allowMapInteractions={false}
                    usePillAnnotations={false}
                    containerStyle={styles.mapContainer}
                  />
                ) : null}
                <View style={styles.listItemContainer}>
                  {listData?.map((item, index) => {
                    const isLastItem = index === listData.length - 1;
                    return (
                      <View key={`${item?.icon}-${item?.text}`}>
                        <ListItem
                          primaryCopy={item?.text ?? ''}
                          showsChevron={false}
                          leadingAccessory={{
                            id: `leading-icon-${index}`,
                            name: VXLComponentId.ICON,
                            props: {
                              iconKey: item?.icon ?? 'ELLIPSE',
                              iconSize: 'MD',
                            },
                          }}
                          trailingAccessory={{
                            id: `trailing-text-${index}`,
                            name: VXLComponentId.LIST_ITEM_TEXT_STACK,
                            props: {
                              primaryCopy: item?.trailingText ?? '',
                              primaryCopyColor: theme.color.text.secondary,
                              lineFormat: ListItemLineFormat.LABEL,
                              textAlignment:
                                ListItemTextStackAlignment.TRAILING,
                            },
                          }}
                        />
                        {!isLastItem && (
                          <View style={styles.divider}>
                            <HairLineDivider />
                          </View>
                        )}
                      </View>
                    );
                  })}
                </View>
                {gasOffers ? (
                  <View style={styles.gasCarouselContainer}>
                    <GasOfferCardCarousel gasOffers={gasOffers} />
                  </View>
                ) : null}
              </View>
              <View style={styles.separator} />
              {howItWorks?.steps ? (
                <>
                  <View style={styles.howItWorksContainer}>
                    <SectionDivider
                      labelText={howItWorks.title ?? null}
                      textSize="large"
                    />
                    <View style={styles.bodyContainer}>
                      <ContentList
                        itemType={ItemType.Number}
                        contentList={howItWorks.steps}
                      />
                    </View>
                  </View>
                  <View style={styles.separator} />
                </>
              ) : null}
              <View style={styles.htmlContentContainer}>
                {getHtmlContents(offer).map((item) => {
                  if (!item.content) {
                    return null;
                  }
                  return (
                    <RenderHtmlOfferSectionView
                      id={item.id}
                      key={item.title}
                      content={item.content}
                      titleText={item.title}
                      showDivider={item.showDivider}
                      linkText={item.linkText ?? ''}
                      handlePdfClick={handlePdfClick}
                    />
                  );
                })}
              </View>
            </>
          ) : null}
        </ScrollView>

        {isActivationError || mutationError ? (
          <View style={styles.snackbar}>
            <Snackbar
              text={translatedText('snackbarText')}
              buttonOnPress="CLOSE"
              duration={5000}
            />
          </View>
        ) : null}

        {showFailureSnackbar ? (
          <View style={styles.snackbar}>
            <Snackbar
              text={failureSnackbarText}
              buttonOnPress="CLOSE"
              duration={5000}
            />
          </View>
        ) : null}
        <View style={styles.buttonContainer}>
          {offer?.primaryButton ? (
            <Button
              label={capitalizeWord(offer?.primaryButton?.buttonText)}
              size="LARGE"
              type={offer?.primaryButton?.buttonStyle ?? 'PRIMARY'}
              width="FULL"
              onPress={getPrimaryButtonHandler()}
              isLoading={
                offer?.primaryButton?.buttonOnClick.__typename ===
                  ButtonComponentActionResultTypenames.ActivateOfferAction &&
                mutationFetching
              }
              isDisabled={
                offer?.primaryButton?.buttonOnClick.__typename ===
                  ButtonComponentActionResultTypenames.ActivateOfferAction &&
                isActivationSuccess
              }
            />
          ) : null}

          {offer?.secondaryButton ? (
            <Button
              label={capitalizeWord(offer?.secondaryButton?.buttonText)}
              size="LARGE"
              type={offer?.secondaryButton?.buttonStyle ?? 'SECONDARY'}
              width="FULL"
              onPress={
                offer?.secondaryButton?.buttonOnClick.__typename ===
                ButtonComponentActionResultTypenames.DeactivateOfferAction
                  ? handleOnDeactivateClick
                  : undefined
              }
            />
          ) : null}
        </View>

        <DeactivateBottomSheet
          bottomSheetRef={deactivateBottomSheetRef}
          onConfirm={handleDeactivateConfirm}
          onError={handleDeactivateError}
          offerId={offer?.id}
        />

        <OfferActivatedBottomSheet
          bottomSheetRef={activateOfferRef}
          offerDetails={offerActivationDetails}
          onSubmit={() => {
            activateOfferRef.current?.dismiss();
            handleGetDirectionsClick();
            reload();
            setActivationSuccessAnalytics(null);
          }}
          onDismiss={() => {
            reload();
            setActivationSuccessAnalytics(null);
          }}
        />

        <MaximumReachedBottomSheet
          bottomSheetRef={maximumReachedBottomSheetRef}
          alertDetails={
            activationMaxReached || dailyMaxReached
              ? {
                  title: activationResult?.header ?? '',
                  message:
                    activationResult?.body ?? activationResult?.message ?? '',
                  primaryButton: {
                    buttonText: activationResult?.button?.buttonText,
                  },
                }
              : undefined
          }
          onSubmit={handleMaximumReachedSubmit}
        />

        <ScreenTransitionOverlay
          LoadingComponent={
            <View style={{ paddingTop: insets.top }}>
              <LoadingSpinner />
            </View>
          }
          isLoading={loading || stale}
          ErrorComponent={
            error ||
            !hasValidParams ||
            !offer ||
            (paused && !loading && !stale && !offer) ? (
              <TabDownErrorMessage
                error={error ?? [translatedText('offerDetailsNotFound')]}
                illustrationKey="HOURGLASS_A"
                tryAgainAction={reload}
              />
            ) : null
          }
        />
      </View>
      <BoostInfoBottomSheet
        bottomSheetRef={boostSheetRef}
        title={offer?.boost?.title ?? ''}
        body={offer?.boost?.body ?? ''}
        primaryButton={{
          buttonText: translatedText('PrimaryButton'),
        }}
        onSubmit={() => boostSheetRef.current?.dismiss()}
        onDismiss={() => {}}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  alertContainer: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
    gap: theme.spacing.s,
  },
  convenientAlertContainer: {
    paddingHorizontal: theme.spacing.m,
    paddingBottom: theme.spacing.m,
  },
  bodyContainer: {
    padding: theme.spacing.m,
    gap: theme.spacing.s,
  },
  buttonContainer: {
    backgroundColor: theme.color.background.primary,
    borderTopColor: theme.color.border.primarySolid,
    borderTopWidth: 1,
    padding: theme.spacing.m,
    gap: theme.spacing.m,
    width: '100%',
  },
  scrollViewContainer: {
    flex: 1,
  },
  divider: {
    marginHorizontal: theme.spacing.m,
  },
  gasCarouselContainer: {
    paddingVertical: theme.spacing.s,
  },
  howItWorksContainer: {
    paddingTop: theme.spacing.s,
    flexDirection: 'column',
    gap: theme.spacing.s,
  },
  htmlContentContainer: {
    backgroundColor: theme.color.background.primary,
  },
  listItemContainer: {
    paddingTop: theme.spacing.s,
  },
  mapContainer: {
    paddingHorizontal: theme.spacing.m,
    height: 120,
  },
  offerInfoContainer: {
    backgroundColor: theme.color.background.primary,
    paddingBottom: theme.spacing.m,
  },
  scrollStyle: {
    flexGrow: 1,
  },
  separator: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  snackbar: {
    bottom: 30,
    left: 0,
    position: 'absolute',
    right: 0,
    zIndex: 1000,
  },
});

export default UpsideDetailsScreen;
